import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { GettingStartedTips } from "../components/GettingStartedTips";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Award, Plus, Search, Star, User, X, Zap } from "lucide-react";

const PROF_COLORS = ["", "var(--app-text-muted,#71717A)", "var(--app-amber,#F59E0B)", "var(--app-blue,#3B82F6)", "var(--app-purple,#8B5CF6)", "var(--app-green,#10B981)"];
const PROF_LABELS = ["", "Beginner", "Basic", "Intermediate", "Advanced", "Expert"];

export default function SkillMatrixPage() {
  const { language, t, ti } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newSkill, setNewSkill] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestForm, setSuggestForm] = useState({ skills: "", position: "", start_date: "", end_date: "" });
  const [showSuggest, setShowSuggest] = useState(false);

  const fetchMatrix = async () => {
    try {
      const r = await api.get("/crew/skill-matrix");
      setData(r.data);
    } catch { toast.error(t("Error loading skill matrix")); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMatrix(); }, []);

  const updateSkill = async (crewId, skillName, proficiency, certified) => {
    try {
      const r = await api.get(`/crew/${crewId}`);
      const current = r.data;
      const skills = [...(current.skills || [])];
      const idx = skills.findIndex(s => s.skill === skillName);
      if (idx >= 0) {
        skills[idx] = { skill: skillName, proficiency, certified: certified !== undefined ? certified : skills[idx].certified };
      } else {
        skills.push({ skill: skillName, proficiency: proficiency || 1, certified: certified || false });
      }
      await api.put(`/crew/${crewId}`, { skills });
      toast.success(ti(`Skill "${skillName}" updated`, `Habilidad "${skillName}" actualizada`, `Fähigkeit "${skillName}" aktualisiert`));
      await fetchMatrix();
      setEditingCell(null);
    } catch (err) {
      console.error("Error updating skill:", err);
      toast.error(t("Error updating skill"));
    }
  };

  const addGlobalSkill = async () => {
    if (!newSkill.trim()) return;
    if (!data.matrix || data.matrix.length === 0) {
      toast.error(t("Add crew members first"));
      return;
    }
    try {
      // Add the new skill to ALL crew members with proficiency 0 so it appears as a column
      const crewId = data.matrix[0].crew_id;
      const r = await api.get(`/crew/${crewId}`);
      const current = r.data;
      const skills = [...(current.skills || [])];
      if (!skills.find(s => s.skill === newSkill.trim())) {
        skills.push({ skill: newSkill.trim(), proficiency: 0, certified: false });
        await api.put(`/crew/${crewId}`, { skills });
      }
      toast.success(ti(`Skill "${newSkill.trim()}" added`, `Habilidad "${newSkill.trim()}" agregada`, `Fähigkeit "${newSkill.trim()}" hinzugefügt`));
      setNewSkill("");
      await fetchMatrix();
    } catch (err) {
      console.error("Error adding skill:", err);
      toast.error(t("Error adding skill"));
    }
  };

  const getSuggestions = async () => {
    try {
      const params = new URLSearchParams();
      if (suggestForm.skills) params.set("skills", suggestForm.skills);
      if (suggestForm.position) params.set("position", suggestForm.position);
      if (suggestForm.start_date) params.set("start_date", suggestForm.start_date);
      if (suggestForm.end_date) params.set("end_date", suggestForm.end_date);
      const r = await api.get(`/crew/suggest?${params}`);
      setSuggestions(r.data.suggestions || []);
    } catch { toast.error(t("Error getting suggestions")); }
  };

  if (loading || !data) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" /></div>;

  const filteredMatrix = data.matrix.filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.positions.some(p => p.toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="space-y-5" data-testid="skill-matrix-page">
      {/* Getting Started */}
      <GettingStartedTips section="skill-matrix" language={language} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--app-text)]">{t("Skill Matrix")}</h1>
          <p className="text-sm text-[var(--app-text-muted)] mt-1">{data.total_crew} {t("crew members")} &middot; {data.skills.length} {t("skills tracked")}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--app-text-muted)]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("Search crew...")} className="pl-8 h-8 text-xs w-48 bg-[var(--app-surface)] border-[var(--app-border)]" data-testid="skill-search" />
          </div>
          <button onClick={() => setShowSuggest(!showSuggest)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${showSuggest ? "bg-violet-500/10 text-violet-400 border-violet-500/30" : "border-[var(--app-border)] text-[var(--app-text-muted)] hover:text-[var(--app-text)]"}`}
            data-testid="toggle-suggest">
            <Zap className="w-3.5 h-3.5" /> {t("AI Suggest")}
          </button>
        </div>
      </div>

      {/* AI Crew Suggestion Panel */}
      {showSuggest && (
        <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4" data-testid="suggest-panel">
          <h3 className="text-sm font-semibold text-violet-400 mb-3 flex items-center gap-1.5"><Zap className="w-4 h-4" /> {t("Crew Assignment Suggestion")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-[10px] text-[var(--app-text-muted)] uppercase mb-1 block">{t("Required Skills")}</label>
              <Input value={suggestForm.skills} onChange={e => setSuggestForm(f => ({ ...f, skills: e.target.value }))} placeholder={t("Lighting, Sound")} className="text-xs h-8 bg-[var(--app-surface)] border-[var(--app-border)]" data-testid="suggest-skills" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--app-text-muted)] uppercase mb-1 block">{t("Position")}</label>
              <Input value={suggestForm.position} onChange={e => setSuggestForm(f => ({ ...f, position: e.target.value }))} placeholder={t("Director")} className="text-xs h-8 bg-[var(--app-surface)] border-[var(--app-border)]" data-testid="suggest-position" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--app-text-muted)] uppercase mb-1 block">{t("Start Date")}</label>
              <Input type="date" value={suggestForm.start_date} onChange={e => setSuggestForm(f => ({ ...f, start_date: e.target.value }))} className="text-xs h-8 bg-[var(--app-surface)] border-[var(--app-border)]" data-testid="suggest-start" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--app-text-muted)] uppercase mb-1 block">{t("End Date")}</label>
              <Input type="date" value={suggestForm.end_date} onChange={e => setSuggestForm(f => ({ ...f, end_date: e.target.value }))} className="text-xs h-8 bg-[var(--app-surface)] border-[var(--app-border)]" data-testid="suggest-end" />
            </div>
          </div>
          <Button size="sm" onClick={getSuggestions} className="bg-violet-600 hover:bg-violet-700 text-white text-xs" data-testid="suggest-btn">{t("Find Best Matches")}</Button>

          {suggestions && (
            <div className="mt-4 space-y-2" data-testid="suggest-results">
              {suggestions.length === 0 ? (
                <p className="text-xs text-[var(--app-text-muted)]">{t("No matches found")}</p>
              ) : suggestions.slice(0, 5).map((s, i) => (
                <div key={s.crew_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[var(--app-surface)] border border-[var(--app-border)]" data-testid={`suggest-result-${s.crew_id}`}>
                  <span className="text-sm font-bold text-[var(--app-text-muted)] w-5 text-center">{i + 1}</span>
                  <div className="w-7 h-7 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0">
                    {s.photo_url ? <img src={s.photo_url} className="w-7 h-7 rounded-full object-cover" alt="" /> : <User className="w-3.5 h-3.5 text-violet-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[var(--app-text)]">{s.name}</p>
                    <p className="text-[10px] text-[var(--app-text-muted)]">{s.positions.join(", ")}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${s.available ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                      {s.available ? t("Available") : t("Busy")}
                    </span>
                    <div className="relative w-9 h-9">
                      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                        <circle cx="18" cy="18" r="14" fill="none" stroke="var(--app-border)" strokeWidth="3" />
                        <circle cx="18" cy="18" r="14" fill="none" stroke={s.match_score >= 70 ? "var(--app-green)" : s.match_score >= 40 ? "var(--app-amber)" : "var(--app-red)"} strokeWidth="3" strokeDasharray={`${s.match_score * 0.88} ${88 - s.match_score * 0.88}`} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold font-mono" style={{ color: s.match_score >= 70 ? "var(--app-green)" : s.match_score >= 40 ? "var(--app-amber)" : "var(--app-red)" }}>{s.match_score}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Proficiency Legend */}
      <div className="flex items-center gap-4 text-[10px]">
        {PROF_LABELS.slice(1).map((l, i) => (
          <span key={l} className="flex items-center gap-1 text-[var(--app-text-muted)]">
            <span className="w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: PROF_COLORS[i + 1] }}>{i + 1}</span>
            {language === "de" ? ["Anfänger", "Grundkenntnisse", "Mittelstufe", "Fortgeschritten", "Experte"][i] : language === "es" ? ["Principiante", "Básico", "Intermedio", "Avanzado", "Experto"][i] : l}
          </span>
        ))}
        <span className="flex items-center gap-1 text-[var(--app-text-muted)]">
          <Award className="w-3 h-3 text-amber-400" /> {t("Certified")}
        </span>
      </div>

      {/* Add Skill */}
      <div className="flex items-center gap-2">
        <Input value={newSkill} onChange={e => setNewSkill(e.target.value)} placeholder={t("Add new skill...")} className="h-8 text-xs w-48 bg-[var(--app-surface)] border-[var(--app-border)]"
          onKeyDown={e => { if (e.key === "Enter") addGlobalSkill(); }}
          data-testid="add-skill-input" />
        <Button size="sm" onClick={addGlobalSkill} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="add-skill-btn"><Plus className="w-3 h-3 mr-1" /> {t("Add Skill")}</Button>
      </div>

      {/* Matrix Grid */}
      {data.skills.length === 0 ? (
        <div className="text-center py-12 bg-[var(--app-surface)] rounded-xl border border-[var(--app-border)]">
          <Star className="w-8 h-8 text-[var(--app-text-muted)] mx-auto mb-2 opacity-30" />
          <p className="text-sm text-[var(--app-text-muted)]">{t("No skills tracked yet. Add your first skill above.")}</p>
        </div>
      ) : (
        <div className="bg-[var(--app-surface)] rounded-xl border border-[var(--app-border)] overflow-x-auto" data-testid="skill-matrix-grid">
          <table className="w-full text-left min-w-[600px]">
            <thead>
              <tr className="border-b border-[var(--app-border)]">
                <th className="px-3 py-2.5 text-[10px] uppercase text-[var(--app-text-muted)] font-semibold tracking-wider sticky left-0 bg-[var(--app-surface)] min-w-[150px]">{t("Crew Member")}</th>
                {data.skills.map(sk => (
                  <th key={sk} className="px-2 py-2.5 text-center">
                    <span className="text-[10px] uppercase text-[var(--app-text-muted)] font-semibold tracking-wider whitespace-nowrap">{sk}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--app-border)]">
              {filteredMatrix.map(m => (
                <tr key={m.crew_id} className="hover:bg-[var(--app-surface-hover)] transition-colors" data-testid={`skill-row-${m.crew_id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-[var(--app-surface)]">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                        {m.photo_url ? <img src={m.photo_url} className="w-6 h-6 rounded-full object-cover" alt="" /> : <User className="w-3 h-3 text-blue-400" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[var(--app-text)] truncate">{m.name}</p>
                        <p className="text-[9px] text-[var(--app-text-muted)] truncate">{m.positions.join(", ")}</p>
                      </div>
                    </div>
                  </td>
                  {data.skills.map(sk => {
                    const cell = m.skills[sk] || { proficiency: 0, certified: false };
                    const isEditing = editingCell === `${m.crew_id}-${sk}`;
                    return (
                      <td key={sk} className="px-1 py-1 text-center">
                        {isEditing ? (
                          <div className="flex flex-col items-center gap-1 p-1">
                            <div className="flex gap-0.5">
                              {[0, 1, 2, 3, 4, 5].map(lvl => (
                                <button key={lvl} onClick={() => updateSkill(m.crew_id, sk, lvl, cell.certified)}
                                  className={`w-5 h-5 rounded text-[9px] font-bold transition-colors ${lvl === cell.proficiency ? "ring-2 ring-blue-400" : ""}`}
                                  style={{ backgroundColor: lvl === 0 ? "var(--app-border)" : PROF_COLORS[lvl], color: lvl === 0 ? "var(--app-text-muted)" : "white" }}
                                  data-testid={`skill-lvl-${lvl}`}>{lvl}</button>
                              ))}
                            </div>
                            <button onClick={() => updateSkill(m.crew_id, sk, cell.proficiency, !cell.certified)} className={`text-[9px] flex items-center gap-0.5 ${cell.certified ? "text-amber-400" : "text-[var(--app-text-muted)]"}`}>
                              <Award className="w-2.5 h-2.5" /> {cell.certified ? t("Certified") : t("Certify")}
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setEditingCell(`${m.crew_id}-${sk}`)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto transition-colors hover:ring-2 hover:ring-blue-400/50 relative"
                            style={{ backgroundColor: cell.proficiency > 0 ? PROF_COLORS[cell.proficiency] + "20" : "transparent" }}
                            data-testid={`skill-cell-${m.crew_id}-${sk}`}>
                            {cell.proficiency > 0 ? (
                              <span className="text-xs font-bold" style={{ color: PROF_COLORS[cell.proficiency] }}>{cell.proficiency}</span>
                            ) : (
                              <span className="text-[10px] text-[var(--app-text-muted)] opacity-30">-</span>
                            )}
                            {cell.certified && <Award className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 text-amber-400" />}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
